import { NgClass } from '@angular/common';
import { Component, EventEmitter, forwardRef, inject, Injector, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { REGEXP_CONFIG } from '@app/core/config/regexp.config';
import { DataTestIdDirective } from '@app/shared/directives/data-test-id.directive';

@Component({
  selector: 'input-field',
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, FormsModule, NgClass, DataTestIdDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true
    }
  ],
  templateUrl: './input-field.component.html',
  styleUrl: './input-field.component.scss'
})
export class InputFieldComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() labelName: string = '';
  @Input() required: boolean = false;
  @Input() id: string = 'input-field' + Date.now().toString(36) + Math.random().toString(36);
  @Input() type = 'text';
  @Input() errorMessage: string = 'Обязательное поле';
  @Input() placeholder: string = 'Введите данные';
  @Input() name: string = '';
  @Input() value?: string | null = null;
  @Input() disabled: boolean = false;
  @Input() readonly = false;
  @Input() plaintext = false;
  @Input() maxLength = Number.MAX_SAFE_INTEGER + 1000;
  @Input() minLength = 1;
  @Input() step?: number | string;
  @Input() min?: number | string;
  @Input() max?: number | string;
  @Input() pattern = REGEXP_CONFIG.ANY_CHARS;
  @Input() control = new FormControl();
  @Input() checkCyrillic: boolean = false;
  @Input() cyrillicWarningMessage: string = 'Содержит кириллицу';
  @Input() uppercase: boolean = true;
  @Input() testId: string = '';

  private injector = inject(Injector);

  @Output() keyPress = new EventEmitter<KeyboardEvent>();
  @Output() focus = new EventEmitter<FocusEvent>();
  @Output() blur = new EventEmitter<FocusEvent>();

  showPassword: boolean = false;
  cyrillicChars: string = '';

  ngOnInit() {
    if (this.plaintext) this.readonly = true;
    if (this.checkCyrillic) this.checkForCyrillic(this.value || '');
    if (!this.testId && !this.name) {
      const ngControl = this.injector.get(NgControl, null);
      if (ngControl?.name) {
        this.testId = String(ngControl.name);
      }
    }
  }

  onChange = (value: string) => {
    if (this.uppercase) {
      value = value.toUpperCase();
    }
    this.control.setValue(value);
    this.control.markAsTouched(); // Ensure validation is triggered
    this.control.updateValueAndValidity(); // Force validation check
    this.checkForCyrillic(value);
  };

  onTouched = () => {};

  writeValue(value: any): void {
    if (value !== undefined) {
      this.value = this.formatValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = (value: string) => {
      if (this.uppercase) {
        value = value.toUpperCase();
      }
      fn(value);
      this.control.setValue(value);
      this.control.markAsTouched(); // Ensure validation is triggered
      this.control.updateValueAndValidity(); // Force validation check
    };
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onKeyPress(event: KeyboardEvent) {
    this.keyPress.emit(event);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const cursorPosition = inputElement.selectionStart;
    const inputChar = event.key;
    const pattern = new RegExp(this.pattern);

    // Prevent negative numbers for numeric fields
    if (
      (this.type === 'number' || this.pattern === '^[0-9]+$' || this.pattern === '^[0-9.]+$') &&
      (inputChar === '-' || inputChar === '+')
    ) {
      event.preventDefault();
      return;
    }

    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }

    if (inputChar === ' ' && cursorPosition === 0) {
      event.preventDefault();
      return;
    }

    if (inputChar === ' ' && cursorPosition && cursorPosition > 0 && currentValue[cursorPosition - 1] === ' ') {
      event.preventDefault();
      return;
    }
  }

  onBlur(event: FocusEvent) {
    this.onTouched();
    this.control.markAsTouched(); // Ensure validation is triggered
    this.control.updateValueAndValidity(); // Force validation check
    this.blur.emit(event);
  }

  onPaste(event: ClipboardEvent) {
    if (this.readonly || this.disabled) return;
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const currentValue = input.value;
    const combined = currentValue.slice(0, start) + pastedText + currentValue.slice(end);
    const formattedValue = this.formatValue(combined);
    input.value = formattedValue;
    this.value = formattedValue;
    this.onChange(formattedValue);

    // Trigger validation immediately after paste
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control as FormControl;
    return this.required && !control.value ? { required: true } : null;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      if (!this.control?.disabled) {
        this.control?.disable({ emitEvent: false });
      }
    } else {
      if (this.control?.disabled) {
        this.control?.enable({ emitEvent: false });
      }
    }
  }

  getErrorMessage(): string {
    const errors = this.control?.errors;

    if (!errors && !this.required) return '';

    // Перечисление всех возможных ошибок с приоритетом
    const errorMessages: { [key: string]: string } = {
      required: 'Обязательное поле',
      minlength: `Минимальная длина ${errors?.['minlength']?.requiredLength} символов. Длина: ${errors?.['minlength']?.actualLength}`,
      maxlength: `Максимальная длина ${errors?.['maxlength']?.requiredLength} символов. Длина: ${errors?.['maxlength']?.actualLength}`,
      min: `Значение не может быть меньше ${errors?.['min']?.min}. Текущее значение: ${errors?.['min']?.actual}`,
      max: `Значение не может быть больше ${errors?.['max']?.max}. Текущее значение: ${errors?.['max']?.actual}`,
      negative: `Значение не может быть отрицательным. Текущее значение: ${errors?.['negative']?.actual}`,
      evenNumber: 'Введите чётное число, начиная с 2 (2, 4, 6...)',
      pattern: 'Некорректный формат',
      mismatch: 'Пароли не совпадают',
      notDifferent: 'Пароль не должен совпадать с текущим паролем'
    };

    // Если поле обязательно, но ошибок нет, возвращаем сообщение 'required'
    if (this.required && !errors) {
      return errorMessages['required'];
    }

    // Возвращаем все найденные ошибки, начиная с первой
    for (const key of Object.keys(errorMessages)) {
      if (errors?.[key]) {
        return errorMessages[key];
      }
    }

    return '';
  }

  private formatValue(value: string): string {
    if (!value) return '';

    const pattern = new RegExp(this.pattern);
    const allowsSpaces = pattern.test(' ');

    let formatted = value.toString();

    if (allowsSpaces) {
      formatted = formatted.replace(/\s+$/, '');
      formatted = formatted.replace(/\s+/g, ' ');
    } else {
      formatted = formatted.replace(/\s/g, '');
    }

    formatted = formatted
      .split('')
      .filter((char) => (allowsSpaces ? pattern.test(char) || char === ' ' : pattern.test(char)))
      .join('');

    formatted = formatted.slice(0, this.maxLength);

    if (!pattern.test(formatted)) {
      formatted = '';
    }

    if (this.checkCyrillic) this.checkForCyrillic(formatted);

    return this.uppercase ? formatted.toUpperCase() : formatted;
  }

  checkForCyrillic(value: string): void {
    if (this.checkCyrillic) {
      this.cyrillicChars = this.findCyrillicChars(value);
    } else {
      this.cyrillicChars = '';
    }
  }

  private findCyrillicChars(str: string): string {
    return (str.match(/[а-яА-Я]/g) || []).join('');
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
